import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircleExclamation, faCircleInfo, faXmark } from '@fortawesome/free-solid-svg-icons';

/**
 * Cockpit Toast — the app-wide "that saved" whisper.
 *
 * Bottom-right stack, newest on top, three visible at most. Rises in on the
 * spring easing, fades and sinks out. Auto-dismisses after ~2.4s, pauses on
 * hover, dismisses on click. Honours prefers-reduced-motion via tokens.css.
 *
 *   const toast = useToast();
 *   toast.success('Status → In Progress');
 *   toast.error('Title not saved. Reverted.');
 */

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastOptions {
  tone?: ToastTone;
  /** Short body copy. Name the field: "Assigned to Katie", "Due Sep 12". */
  message: React.ReactNode;
  /** Optional secondary line. */
  detail?: React.ReactNode;
  /** Auto-dismiss delay in ms. Errors default longer. */
  duration?: number;
}

interface ToastItem extends Required<Pick<ToastOptions, 'tone' | 'message' | 'duration'>> {
  id: number;
  detail?: React.ReactNode;
  leaving: boolean;
}

export interface ToastApi {
  show: (opts: ToastOptions) => number;
  success: (message: React.ReactNode, detail?: React.ReactNode) => number;
  error: (message: React.ReactNode, detail?: React.ReactNode) => number;
  info: (message: React.ReactNode, detail?: React.ReactNode) => number;
  dismiss: (id: number) => void;
}

const MAX_VISIBLE = 3;
const EXIT_MS = 220;
const DEFAULT_MS = 2400;
const ERROR_MS = 4200;

const ToastContext = createContext<ToastApi | null>(null);

const noop: ToastApi = {
  show: () => 0,
  success: () => 0,
  error: () => 0,
  info: () => 0,
  dismiss: () => undefined,
};

/** Safe outside the provider (tests, storybook-ish renders): no-ops. */
// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastApi {
  return useContext(ToastContext) || noop;
}

const toneStyles: Record<ToastTone, { color: string; icon: typeof faCheck; label: string }> = {
  success: { color: 'var(--status-up)', icon: faCheck, label: 'Saved' },
  error: { color: 'var(--danger-400)', icon: faCircleExclamation, label: 'Error' },
  info: { color: 'var(--accent)', icon: faCircleInfo, label: 'Info' },
};

function ToastCard({ item, onDismiss, onHover }: { item: ToastItem; onDismiss: () => void; onHover: (over: boolean) => void }) {
  const tone = toneStyles[item.tone];
  return (
    <div
      role={item.tone === 'error' ? 'alert' : 'status'}
      className={`ck-toast${item.leaving ? ' ck-toast-leaving' : ''}`}
      onClick={onDismiss}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '11px',
        minWidth: '240px',
        maxWidth: '360px',
        padding: '11px 12px 11px 13px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        boxShadow: 'var(--shadow-pop)',
        color: 'var(--text-body)',
        cursor: 'pointer',
        pointerEvents: 'auto',
        borderLeft: `2px solid ${tone.color}`,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '20px',
          height: '20px',
          flexShrink: 0,
          borderRadius: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: tone.color,
          background: 'color-mix(in srgb, currentColor 14%, transparent)',
          marginTop: '1px',
        }}
      >
        <FontAwesomeIcon icon={tone.icon} style={{ fontSize: '10px' }} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
        >
          {item.message}
        </span>
        {item.detail ? (
          <span
            style={{
              display: 'block',
              marginTop: '2px',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {item.detail}
          </span>
        ) : null}
      </span>
      <span
        aria-hidden="true"
        style={{
          color: 'var(--text-faint)',
          display: 'inline-flex',
          padding: '2px',
          marginTop: '1px',
          flexShrink: 0,
        }}
      >
        <FontAwesomeIcon icon={faXmark} style={{ fontSize: '10px' }} />
      </span>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const timers = useRef<Map<number, { handle: number; remaining: number; startedAt: number }>>(new Map());

  const remove = useCallback((id: number) => {
    setItems((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) window.clearTimeout(timer.handle);
    timers.current.delete(id);
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      setItems((current) => current.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
      const timer = timers.current.get(id);
      if (timer) window.clearTimeout(timer.handle);
      timers.current.set(id, { handle: window.setTimeout(() => remove(id), EXIT_MS), remaining: 0, startedAt: Date.now() });
    },
    [remove]
  );

  const arm = useCallback(
    (id: number, ms: number) => {
      const handle = window.setTimeout(() => dismiss(id), ms);
      timers.current.set(id, { handle, remaining: ms, startedAt: Date.now() });
    },
    [dismiss]
  );

  const show = useCallback(
    (opts: ToastOptions) => {
      const id = ++seq.current;
      const tone = opts.tone || 'info';
      const duration = opts.duration ?? (tone === 'error' ? ERROR_MS : DEFAULT_MS);
      setItems((current) => {
        const next = [{ id, tone, message: opts.message, detail: opts.detail, duration, leaving: false }, ...current];
        // Newest on top; anything past the cap starts leaving now.
        return next.map((t, i) => (i >= MAX_VISIBLE && !t.leaving ? { ...t, leaving: true } : t));
      });
      arm(id, duration);
      return id;
    },
    [arm]
  );

  // Sweep toasts that were pushed past the cap (they were marked leaving in show()).
  useEffect(() => {
    const overflow = items.filter((t, i) => i >= MAX_VISIBLE && t.leaving);
    if (overflow.length === 0) return;
    const handles = overflow.map((t) => window.setTimeout(() => remove(t.id), EXIT_MS));
    return () => handles.forEach((h) => window.clearTimeout(h));
  }, [items, remove]);

  const hover = useCallback(
    (id: number, over: boolean) => {
      const timer = timers.current.get(id);
      if (!timer) return;
      if (over) {
        window.clearTimeout(timer.handle);
        const elapsed = Date.now() - timer.startedAt;
        timers.current.set(id, { ...timer, remaining: Math.max(400, timer.remaining - elapsed) });
      } else {
        arm(id, timer.remaining);
      }
    },
    [arm]
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => window.clearTimeout(t.handle));
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, detail) => show({ tone: 'success', message, detail }),
      error: (message, detail) => show({ tone: 'error', message, detail }),
      info: (message, detail) => show({ tone: 'info', message, detail }),
      dismiss,
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          className="ck-toast-stack"
          style={{
            position: 'fixed',
            right: 'calc(18px + var(--sar))',
            bottom: 'calc(18px + var(--sab))',
            zIndex: 200,
            display: 'flex',
            flexDirection: 'column-reverse',
            gap: '8px',
            pointerEvents: 'none',
          }}
        >
          {items.slice(0, MAX_VISIBLE).map((item) => (
            <ToastCard key={item.id} item={item} onDismiss={() => dismiss(item.id)} onHover={(over) => hover(item.id, over)} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export default ToastProvider;
